import React, { useState, useEffect } from 'react'
import { X, MapPin, User, Calendar, CheckCircle } from 'lucide-react'
import { Button, Input } from '../common'
import useSurveyStore from '../../stores/surveyStore'
import { formatDate } from '../../utils/helpers'
import { SURVEY_STATUS_LABELS, SURVEY_STATUS_COLORS } from '../../utils/constants'

const SURVEY_LEVELS = [
  { value: 1, label: 'Level 1', description: 'Basic' },
  { value: 2, label: 'Level 2', description: 'Standard' },
  { value: 3, label: 'Level 3', description: 'Full' }
]

export default function SurveyDetailsFlyout({ isOpen, onClose }) {
  const currentSurvey = useSurveyStore(state => state.currentSurvey)
  const currentPhotos = useSurveyStore(state => state.currentPhotos)
  const updateSurvey = useSurveyStore(state => state.updateSurvey)
  const loadSurvey = useSurveyStore(state => state.loadSurvey)

  const [formData, setFormData] = useState({
    propertyName: '',
    address: '',
    postcode: '',
    surveyorName: '',
    surveyLevel: 1
  })
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Load form data when survey changes
  useEffect(() => {
    if (currentSurvey) {
      setFormData({
        propertyName: currentSurvey.propertyName || '',
        address: currentSurvey.address || '',
        postcode: currentSurvey.postcode || '',
        surveyorName: currentSurvey.surveyorName || '',
        surveyLevel: currentSurvey.surveyLevel || 1
      })
      setHasChanges(false)
    }
  }, [currentSurvey])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setHasChanges(true)
  }

  const handleSave = async () => {
    if (!currentSurvey || !hasChanges) return
    setSaving(true)
    try {
      await updateSurvey(currentSurvey.id, formData)
      await loadSurvey(currentSurvey.id)
      setHasChanges(false)
    } catch (error) {
      console.error('Failed to save:', error)
      alert('Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (!currentSurvey) return null

  const photoCount = currentPhotos?.length || 0
  const categoryCount = new Set(currentPhotos?.map(p => p.categoryCode) || []).size

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50"
          onClick={onClose}
        />
      )}

      {/* Flyout Panel */}
      <div
        className={`
          fixed top-0 left-0 h-full w-80 bg-white shadow-2xl z-50
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-primary-700 text-white">
          <h2 className="font-semibold">Survey Details</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-primary-600 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto h-[calc(100%-140px)]">
          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${SURVEY_STATUS_COLORS[currentSurvey.status]}`}>
              {SURVEY_STATUS_LABELS[currentSurvey.status]}
            </span>
            <span className="text-sm text-slate-500">
              {formatDate(currentSurvey.createdAt)}
            </span>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-primary-700">{photoCount}</div>
              <div className="text-xs text-slate-500">Photos</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-primary-700">{categoryCount}</div>
              <div className="text-xs text-slate-500">Categories</div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Property Name</label>
              <input
                type="text"
                name="propertyName"
                value={formData.propertyName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Postcode</label>
              <input
                type="text"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm uppercase focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Surveyor</label>
              <input
                type="text"
                name="surveyorName"
                value={formData.surveyorName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Survey Level */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Survey Level</label>
              <div className="grid grid-cols-3 gap-2">
                {SURVEY_LEVELS.map((level) => (
                  <button
                    key={level.value}
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, surveyLevel: level.value }))
                      setHasChanges(true)
                    }}
                    className={`
                      p-2 rounded-lg border text-center transition-all text-xs
                      ${formData.surveyLevel === level.value
                        ? 'border-primary-700 bg-primary-50 text-primary-700'
                        : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }
                    `}
                  >
                    <div className="font-semibold">{level.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={!hasChanges || saving}
            fullWidth
          >
            {hasChanges ? 'Save Changes' : 'No Changes'}
          </Button>
        </div>
      </div>
    </>
  )
}
